import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../lib/api";
import { FilePdf, CheckCircle, Clock } from "@phosphor-icons/react";

const fmtMXN = (v) => (v || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function PaymentReceiptView({ publicToken }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/cxc/payments/public/${publicToken}`)
      .then(res => setData(res.data))
      .catch(e => setError(e.response?.status === 404 ? "Recibo no encontrado" : "Error al cargar"))
      .finally(() => setLoading(false));
  }, [publicToken]);

  const handlePdf = async () => {
    try {
      const res = await axios.get(`${API}/cxc/payments/public/${publicToken}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Recibo_${data?.payment?.sale_folio || "pago"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch { alert("Error al descargar PDF"); }
  };

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="animate-spin w-10 h-10 border-4 border-sky-600 border-t-transparent rounded-full" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="bg-white rounded-2xl shadow-lg p-8 text-center max-w-sm">
        <p className="text-red-500 font-bold text-lg">{error}</p>
        <p className="text-sm text-slate-500 mt-2">El enlace puede haber expirado o ser invalido.</p>
      </div>
    </div>
  );

  const { payment, config: cfg, payment_method_labels } = data;
  const methodName = payment_method_labels?.[payment.payment_method] || payment.payment_method || "";
  const balanceAfter = payment.balance_after || 0;
  const isFullyPaid = balanceAfter <= 0;
  const tc = cfg?.template_colors || {};
  const primaryColor = tc.primary || "#0F172A";
  const accentColor = tc.accent || "#2563EB";

  let dateStr = "";
  if (payment.created_at) {
    try {
      const d = new Date(payment.created_at);
      dateStr = d.toLocaleDateString("es-MX", { year: "numeric", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { dateStr = payment.created_at; }
  }

  return (
    <div className="min-h-screen bg-slate-100 py-6 px-4" style={{ fontFamily: "'Helvetica Neue', Arial, sans-serif" }} data-testid="receipt-public-view">
      <div className="max-w-[850px] mx-auto bg-white shadow-lg">

        {/* Top color bar */}
        <div style={{ backgroundColor: primaryColor, height: "6px" }} />

        {/* Header: Logo + Company + Doc Type */}
        <div className="px-8 pt-6 pb-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              {cfg?.logo_url && (
                <img src={cfg.logo_url} alt="Logo" className="w-20 h-20 object-contain" />
              )}
              <div>
                <h1 className="text-lg font-bold" style={{ color: primaryColor }}>{cfg?.company_name || "Empresa"}</h1>
                {cfg?.rfc && <p className="text-xs text-gray-600">RFC: {cfg.rfc}</p>}
                {cfg?.phone && <p className="text-xs text-gray-600">Tel.: {cfg.phone}</p>}
                {cfg?.address && <p className="text-xs text-gray-600">{cfg.address} {cfg?.postal_code ? `C.P ${cfg.postal_code}` : ""}</p>}
                {cfg?.tax_regime && <p className="text-xs text-gray-600">Regimen Fiscal: {cfg.tax_regime}</p>}
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="inline-block px-5 py-2 text-white font-bold text-base" style={{ backgroundColor: primaryColor }}>
                RECIBO DE ABONO
              </div>
              <p className="font-bold text-sm mt-2" style={{ color: primaryColor }}>FOLIO: {payment.sale_folio}</p>
            </div>
          </div>
        </div>

        {/* Separator */}
        <div className="mx-8" style={{ borderBottom: `2px solid ${accentColor}` }} />

        {/* Details boxes */}
        <div className="px-8 py-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
            <div className="border p-3" style={{ borderColor: accentColor }}>
              <p className="font-bold text-xs mb-1" style={{ color: primaryColor }}>Cliente</p>
              <p className="font-semibold text-gray-800">{payment.customer_name || "-"}</p>
            </div>
            <div className="border p-3" style={{ borderColor: accentColor }}>
              <p className="font-bold text-xs mb-1" style={{ color: primaryColor }}>Datos del Abono</p>
              <p className="text-gray-600">Fecha: <span className="text-gray-800 font-medium">{dateStr}</span></p>
              <p className="text-gray-600">Metodo: <span className="text-gray-800 font-medium">{methodName}</span></p>
              {payment.reference && <p className="text-gray-600">Ref: <span className="text-gray-800 font-medium">{payment.reference}</span></p>}
            </div>
            <div className="border p-3" style={{ borderColor: accentColor }}>
              <p className="font-bold text-xs mb-1" style={{ color: primaryColor }}>Referencia</p>
              <p className="text-gray-600">Folio Venta: <span className="text-gray-800 font-bold">{payment.sale_folio}</span></p>
              <p className="text-gray-600">Registrado por: <span className="text-gray-800 font-medium">{payment.created_by_name || "-"}</span></p>
            </div>
          </div>
        </div>

        {/* Amount + Balance Section */}
        <div className="px-8 py-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left: Amount big */}
            <div className="flex flex-col items-center justify-center py-6">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Monto Abonado</p>
              <p className="text-4xl font-extrabold font-mono" style={{ color: primaryColor }}>${fmtMXN(payment.amount)}</p>
              <p className="text-xs text-gray-400 mt-1">MXN</p>
            </div>

            {/* Right: Balance breakdown */}
            <div>
              <table className="w-full text-sm">
                <tbody>
                  <tr className="border-b">
                    <td className="py-2.5 text-gray-500">Saldo Anterior:</td>
                    <td className="py-2.5 text-right font-mono font-medium text-gray-800">${fmtMXN(payment.balance_before)}</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2.5 font-bold" style={{ color: accentColor }}>Abono Aplicado:</td>
                    <td className="py-2.5 text-right font-mono font-bold" style={{ color: accentColor }}>-${fmtMXN(payment.amount)}</td>
                  </tr>
                  <tr style={{ backgroundColor: primaryColor }}>
                    <td className="py-3 px-2 text-white font-bold">Saldo Restante:</td>
                    <td className="py-3 px-2 text-right font-mono font-bold text-white">${fmtMXN(payment.balance_after)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Notes */}
        {payment.notes && (
          <div className="px-8 py-3">
            <p className="text-xs font-bold mb-1" style={{ color: primaryColor }}>Observaciones</p>
            <p className="text-xs text-gray-700">{payment.notes}</p>
          </div>
        )}

        {/* Status Banner */}
        <div className="mx-8 my-4 rounded-lg overflow-hidden">
          <div className={`px-6 py-3 text-center ${isFullyPaid ? "bg-emerald-600" : "bg-amber-500"}`}>
            <div className="flex items-center justify-center gap-2">
              {isFullyPaid ? <CheckCircle size={20} weight="fill" className="text-white" /> : <Clock size={20} weight="fill" className="text-white" />}
              <span className="text-sm font-bold text-white">
                {isFullyPaid ? "CUENTA LIQUIDADA" : "PAGO PARCIAL - SALDO PENDIENTE"}
              </span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-8 py-4 flex items-center justify-between border-t">
          <button onClick={handlePdf} className="flex items-center gap-2 text-xs font-bold hover:opacity-70 transition" style={{ color: accentColor }} data-testid="receipt-download-pdf">
            <FilePdf size={16} weight="duotone" /> Descargar PDF
          </button>
          <p className="text-[10px] text-gray-400">
            {cfg?.company_name ? `Documento generado por ${cfg.company_name}` : "Comprobante generado por el sistema"}
          </p>
        </div>

        {/* Bottom color bar */}
        <div style={{ backgroundColor: primaryColor, height: "4px" }} />
      </div>
    </div>
  );
}
